import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import {
  getStoredConsent,
  saveConsent,
  buildConsentState,
  type ConsentState,
} from '@/lib/consent';
import {
  initializeSentry,
  isSentryInitialized,
  shutdownSentry,
  captureMessage,
} from '@/lib/sentry-config';
import { getAndClearEarlyErrors } from '@/lib/sentry-early';

interface ConsentContextValue {
  consent: ConsentState | null;
  showBanner: boolean;
  showModal: boolean;
  openModal: () => void;
  closeModal: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (analytics: boolean) => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
};

interface ConsentProviderProps {
  children: React.ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<ConsentState | null>(() => getStoredConsent());
  const [showBanner, setShowBanner] = useState(() => getStoredConsent() === null);
  const [showModal, setShowModal] = useState(false);
  const sentryActivatedRef = useRef(false);

  const applyConsent = useCallback((analytics: boolean) => {
    const newState = buildConsentState(analytics);
    saveConsent(newState);
    setConsent(newState);
    setShowBanner(false);
    setShowModal(false);

    if (analytics && !isSentryInitialized()) {
      initializeSentry();
      sentryActivatedRef.current = true;
      // Flush any errors captured before Sentry was initialised
      const queued = getAndClearEarlyErrors();
      if (queued.length > 0) {
        queued.forEach((rec) => {
          captureMessage(`Early ${rec.type}: ${rec.message}`, 'error');
        });
      }
    } else if (!analytics && isSentryInitialized()) {
      shutdownSentry();
      sentryActivatedRef.current = false;
    }

    // GA4: update Consent Mode and initialise/stop accordingly
    if (analytics) {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { 'analytics_storage': 'granted' });
      }
      const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
      if (measurementId) {
        ReactGA.initialize(measurementId);
      }
    } else {
      if (typeof window.gtag === 'function') {
        window.gtag('consent', 'update', { 'analytics_storage': 'denied' });
      }
      // GA4 stops collecting via Consent Mode — no explicit shutdown needed
    }
  }, []);

  const acceptAll = useCallback(() => applyConsent(true), [applyConsent]);
  const rejectAll = useCallback(() => applyConsent(false), [applyConsent]);
  const savePreferences = useCallback(
    (analytics: boolean) => applyConsent(analytics),
    [applyConsent]
  );

  const openModal = useCallback(() => setShowModal(true), []);
  const closeModal = useCallback(() => setShowModal(false), []);

  return (
    <ConsentContext.Provider
      value={{
        consent,
        showBanner,
        showModal,
        openModal,
        closeModal,
        acceptAll,
        rejectAll,
        savePreferences,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};
